<template>
  <FreeBottomSheet :name="name" @onOpen="searchHistory.init">
    <template #header="{ hide }">
      <FreeHeader class="p-4">
        <h2
          class="text-text-neutral-primary text-base font-medium"
        >
          {{ title }}
        </h2>
        <template #rightAction>
          <FreeCloseButton
            @click="hide"
          />
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <div class="min-h-[80vh] px-4 pb-5">
        <FreeSearchInput
          class="bg-background-neutral-primary sticky top-0 pb-5"
          :model-value="citiesSearchModel.query.value"
          :placeholder="searchPlaceholder"
          @onInput="handleCityInput"
          @keydown.enter.prevent="handleEnter"
        >
          <template #prepend>
            <!-- prettier-ignore -->
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none"><path stroke="#A8ACB1" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8.283 14.504a6.22 6.22 0 1 1 0-12.441 6.22 6.22 0 0 1 0 12.44ZM16.062 16.063l-3.11-3.11"/></svg>
          </template>
          <template #clearButton>
            <FreeCloseButton
            />
          </template>
        </FreeSearchInput>
        <div v-if="!citiesSearchModel.query.value" class="space-y-2">
          <CitiesList
            v-if="isSearchHistoryVisible"
            class="bg-background-neutral-secondary rounded-2xl px-4 pt-4"
            :title="$t('main.cities.searchHistory')"
            :cities-list="searchHistory.citiesList.value"
            :is-children-hidden="isChildrenHidden"
            @onCitySelect="handleCitySelect"
          />
          <CitiesList
            v-if="isPopularRoutesVisible"
            class="bg-background-neutral-secondary rounded-2xl px-4 pt-4"
            :title="$t('main.cities.popularDestinations')"
            :cities-list="popularRoutes"
            @onCitySelect="handleCitySelect"
          />
        </div>
        <FreeLoader
          v-else-if="citiesSearchModel.isLoading.value"
          class="mx-auto mt-[40%]"
        />
        <CitiesList
          v-else-if="citiesSearchModel.cities.value.length > 0"
          class="bg-background-neutral-secondary rounded-2xl px-4 pt-4"
          :title="$t('main.cities.title')"
          :cities-list="citiesSearchModel.cities.value"
          @onCitySelect="handleCitySelect"
        >
          <slot name="subDirectionIcon" />
        </CitiesList>
        <EmptyResults
          v-else-if="citiesSearchModel.isEmptyResults.value"
          :title="t('citySelectBottomSheet.emptySearchResultsTitle')"
          :description="t('citySelectBottomSheet.tryAgain')"
        >
          <template #action>
            <button
              type="button"
              class="text-button-primary-default text-base font-semibold"
              @click="onResetClick"
            >
              {{ t('citySelectBottomSheet.clearInput') }}
            </button>
          </template>
        </EmptyResults>
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { citySearchHistory, searchCities } from 'shared';
  import { useI18n } from 'vue-i18n';
  import type { MappedLocation, MappedCity } from 'shared';
  import { computed } from 'vue';

  import {
    FreeBottomSheet,
    FreeHeader,
    FreeCloseButton,
    FreeSearchInput,
    FreeLoader,
  } from '../../../shared/ui';
  import { EmptyResults } from '../../../entities';
  import CitiesList from './CitiesList.vue';

  const props = defineProps<{
    name: string;
    title: string;
    searchPlaceholder: string;
    minQueryLength: number;
    popularRoutes: MappedCity[];
    searchHistoryPrefix: string;
    fetchCities: (query: string) => Promise<MappedCity[]>;
    onlyKzCities?: boolean;
    shouldShowHistory?: boolean;
    isChildrenHidden?: boolean;
    shouldShowPopularRoutes?: boolean;
  }>();

  const emit = defineEmits<{
    onCitySelect: [city: MappedLocation];
  }>();

  const { t } = useI18n();

  const searchHistory = citySearchHistory.model.useCitySearchHistory(
    props.searchHistoryPrefix,
  );

  const citiesSearchModel = searchCities.model.useCitiesSearch(
    props.fetchCities,
  );

  const isSearchHistoryVisible = computed(
    () =>
      props.shouldShowHistory && searchHistory.citiesList.value.length !== 0,
  );

  const isPopularRoutesVisible = computed(
    () => !isSearchHistoryVisible.value || props.shouldShowPopularRoutes,
  );

  const handleCityInput = (query: string) => {
    const trimmedQuery = query.trim();
    citiesSearchModel.setNewQuery(trimmedQuery);
    if (trimmedQuery.length <= props.minQueryLength) {
      return;
    }
    if (citiesSearchModel.query.value) {
      citiesSearchModel.searchCities(citiesSearchModel.query.value);
    } else {
      citiesSearchModel.clearCities();
    }
  };

  const handleCitySelect = (city: MappedLocation) => {
    emit('onCitySelect', city);
    citiesSearchModel.setNewQuery('');
    citiesSearchModel.clearCities();
  };

  const getFirstVisibleCity = (): MappedLocation | null => {
    if (!citiesSearchModel.query.value) {
      if (
        props?.shouldShowHistory &&
        searchHistory.citiesList.value.length !== 0
      ) {
        return searchHistory.citiesList.value[0];
      }
      if (props.popularRoutes.length > 0) {
        return props.popularRoutes[0];
      }
    }
    if (citiesSearchModel.isLoading.value) return null;

    if (citiesSearchModel.isEmptyResults.value) return null;

    if (citiesSearchModel.cities.value.length > 0) {
      return citiesSearchModel.cities.value[0];
    }

    return null;
  };

  const handleEnter = () => {
    const city = getFirstVisibleCity();
    if (city) {
      handleCitySelect(city);
    }
  };

  const onResetClick = () => {
    citiesSearchModel.setNewQuery('');
    citiesSearchModel.clearCities();
  };
</script>

<i18n src="../config/locales.json"></i18n>
